import { Token } from '@stripe/stripe-js'
import { useEffect, useState } from 'react'

import { Source } from '../../models/Source'
import { User } from '../../models/User'
import { CardModal } from '../payments/CardModal'
import { SourceModal } from './SourceModal'
import { SourcesModal } from './SourcesModal'

type Props = {
    isOpen: boolean
    onClose: () => void
    sources: Source[]
    onCreateSource:
        | { action: 'create-source'; client: User; callback: (source: Source) => void }
        | { action: 'return-source-token'; callback: (token: Token) => void }
    onDeleteSource: (source: Source) => void
    onSelectSource?: (source: Source) => void
    selectedSourceID?: string | null
}

export const SourceModalWorkflow = ({
    isOpen,
    onClose,
    sources,
    onCreateSource,
    onDeleteSource,
    onSelectSource,
    selectedSourceID,
}: Props) => {
    const [viewState, setViewState] = useState<
        { type: 'source-list' } | { type: 'source-detail'; source: Source } | { type: 'new-source' }
    >({ type: 'source-list' })

    useEffect(() => {
        if (!isOpen) {
            return
        }
        setViewState(sources.length > 0 ? { type: 'source-list' } : { type: 'new-source' })
    }, [isOpen])

    const handleDeleteSource = async (source: Source) => {
        onDeleteSource(source)
        setViewState({ type: 'source-list' })
    }

    switch (viewState.type) {
        case 'source-list': {
            return (
                <SourcesModal
                    isOpen={isOpen}
                    onClose={onClose}
                    sources={sources ?? []}
                    onSelect={(source) => {
                        if (onSelectSource) {
                            onSelectSource(source)
                        }
                        else {
                            setViewState({ type: 'source-detail', source })
                        }
                    }}
                    onAddNew={() => setViewState({ type: 'new-source' })}
                    selectedSourceID={selectedSourceID}
                />
            )
        }
        case 'source-detail': {
            return (
                <SourceModal
                    isOpen={isOpen}
                    onClose={onClose}
                    source={viewState.source}
                    onDelete={() => handleDeleteSource(viewState.source)}
                    onShowList={() => setViewState({ type: 'source-list' })}
                />
            )
        }
        case 'new-source': {
            return (
                <CardModal
                    isOpen={isOpen}
                    onClose={onClose}
                    onSuccess={
                        onCreateSource?.action === 'create-source'
                            ? {
                                  action: 'create-source',
                                  client: onCreateSource.client,
                                  callback: (source) => {
                                      onCreateSource.callback(source)
                                      setViewState({ type: 'source-list' })
                                  },
                              }
                            : {
                                  action: 'return-source-token',
                                  callback: (token) => {
                                      onCreateSource.callback(token)
                                      setViewState({ type: 'source-list' })
                                  },
                              }
                    }
                    onBack={() => {
                        setViewState({ type: 'source-list' })
                    }}
                />
            )
        }
    }
}
